import { Controller, useFormContext } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'

import Modals from 'singletons/Modals'
import { FormInput, FormCheckbox, FormDatePicker } from 'components/common/form'
import ErrorFieldForm from 'components/UI/ErrorFieldForm'

import { startOfDay } from 'date-fns'
import classNames from '@ui-components-3/ui/lib/utils/classNames'
import { Medication } from 'types/PrivateData/MedicalHistory/Medication'
import { useCallback } from 'react'
import User from 'singletons/User'

const PriorHeader = ({ errors }: { errors: Record<string, string> }) => {
  return (
    <p
      className={classNames('typography-caption my-4', errors?.priorAuthNumber ? 'text-unnamed-4' : 'text-fuchsia-600')}
    >
      Prior authorization number*
    </p>
  )
}

type InputWithActionsProps = {
  isPriorAuthType: boolean
  putApproveChangeRequest: () => void
  putDenyChangeRequest: () => void
  prescriberAuthorizationDataLabel?: string
  prescriberAuthorizationDataType?: string
  isPrescriberAuthType: boolean
  showPrescriberLicenseInactiveCheckbox: boolean
  // TODO - improve requestedMedications style
  requestedMedications: any[]
  prescribedMedication: Medication
}

const InputWithActions = ({
  isPriorAuthType,
  putApproveChangeRequest,
  putDenyChangeRequest,
  prescriberAuthorizationDataLabel,
  prescriberAuthorizationDataType,
  isPrescriberAuthType,
  showPrescriberLicenseInactiveCheckbox,
  requestedMedications,
  prescribedMedication,
}: InputWithActionsProps) => {
  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors, isDirty },
  } = useFormContext()

  const handleDenyClick = () => {
    const modalKey = User.getFeatureFlagByName('pwUiComponents3PrescriptionForm')
      ? 'denyRxChangeRequest'
      : 'denyRxChangeRequestOld'
    Modals.open(modalKey, {
      putDenyChangeRequest,
    })
  }

  const submitHandler = ({
    priorAuthNumber,
    selectedIndex,
    prescriberAuthorizationData,
    prescriberLicenseInactive,
  }) => {
    const modalKey = User.getFeatureFlagByName('pwUiComponents3PrescriptionForm')
      ? 'confirmRxChangeRequest'
      : 'confirmRxChangeRequestOld'

    Modals.open(modalKey, {
      putApproveChangeRequest,
      selectedMedicationIndex: selectedIndex,
      priorAuthNumber,
      prescriberAuthorizationData,
      prescriberLicenseInactive,
    })
  }

  const selectedMedicationIndex = watch('selectedIndex')

  const openEditPrescriptionForm = useCallback(() => {
    const modalKey = User.getFeatureFlagByName('pwUiComponents3PrescriptionForm')
      ? 'editRxChangePrescription'
      : 'editRxChangePrescriptionOld'

    Modals.open(modalKey, {
      medication: selectedMedicationIndex >= 0 ? requestedMedications[selectedMedicationIndex] : prescribedMedication,
      putApproveChangeRequest,
      selectedMedicationIndex,
    })
  }, [prescribedMedication, putApproveChangeRequest, requestedMedications, selectedMedicationIndex])

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      {isPriorAuthType && (
        <>
          <PriorHeader errors={errors} />
          <FormInput {...register('priorAuthNumber')} className="mt-4" />
        </>
      )}
      {isPrescriberAuthType ? (
        prescriberAuthorizationDataType === 'date' ? (
          <>
            <p className="typography-body-l">{prescriberAuthorizationDataLabel}</p>
            <Controller
              name={'prescriberAuthorizationData'}
              control={control}
              render={({ field }) => (
                <FormDatePicker
                  {...field}
                  autoComplete={'off'}
                  allowEmptyValue
                  label="MM / DD / YYYY"
                  clearable
                  variant="dialog"
                  className="[&_svg]:w-[1em] [&_svg]:h-[1em] [&_svg]:mr-0"
                  onChange={(date) => {
                    if (date) {
                      if (date != 'Invalid Date') {
                        date = new Date(date)
                        const isoDate = startOfDay(date).toISOString().replace('Z', '')
                        field.onChange(isoDate)
                      }
                    } else {
                      field.onChange('')
                    }
                  }}
                />
              )}
            />
          </>
        ) : prescriberAuthorizationDataType === 'text' ? (
          <FormInput
            autoComplete={'off'}
            label={prescriberAuthorizationDataLabel}
            {...register('prescriberAuthorizationData')}
          />
        ) : null
      ) : null}
      {showPrescriberLicenseInactiveCheckbox ? (
        <FormCheckbox
          className="mt-2.5"
          label="License is inactive but prescriptive authority is maintained by state or federal regulations"
          {...register('prescriberLicenseInactive')}
        />
      ) : null}
      <ErrorMessage errors={errors} name="priorAuthNumber" as={<ErrorFieldForm />} />
      <ErrorMessage errors={errors} name="prescriberAuthorizationData" as={<ErrorFieldForm />} />
      <ErrorMessage errors={errors} name="selectedIndex" as={<ErrorFieldForm />} />

      <div className="flex items-center md:justify-end flex-wrap gap-4 mt-4">
        <button type="button" className="btn-md btn-fuchsia-outlined" onClick={handleDenyClick}>
          Deny
        </button>
        <button
          type="submit"
          className="btn-md btn-primary"
          disabled={!isDirty && (isPriorAuthType || isPrescriberAuthType)}
        >
          Approve
        </button>
        <button
          type="button"
          className="btn-md btn-primary"
          disabled={!isDirty && (isPriorAuthType || isPrescriberAuthType)}
          onClick={openEditPrescriptionForm}
        >
          Approve with Changes
        </button>
      </div>
    </form>
  )
}

export default InputWithActions
